import { useEffect, useState } from 'react';
import { hideAppSpinner, showAppNotification, showAppSpinner } from '@rainbow-modules/app';
import ButtonIcon from 'components/ButtonIcon';
import Download from 'components/icons/download';
import SmsIcon from 'components/icons/sms';
import useTwilioConversation from 'hooks/useTwilioConversation';
import {
    Ban, CheckCircle2, History, Printer, Send, Signature, Sticker,
} from 'lucide-react';
import {
    MenuItem, RenderIf, Spinner, Tabset,
} from 'react-rainbow-components';
import ButtonMenu from 'components/ButtonMenu';
import DotsVertical from 'components/icons/dotsVertical';
import Eye from 'components/icons/eye';
import Pencil from 'components/icons/pencil2';
import ListDetails from 'components/icons/listDetails';
import Note from 'components/icons/note';
import InvoiceDocumentView from '../InvoiceDocumentView';
import { InvoiceStatusBadge } from '../InvoiceList/columns/status';
import { loadInvoiceAcceptance } from '../api/client';
import downloadInvoice, {
    downloadPrestigeSticker,
    renderPrestigeStickerPdf,
} from '../downloadInvoice';
import printInvoice, { printPdfBlob } from '../printInvoice';
import { isPrestigeInvoiceAgent } from '../InvoicePdf/prestige';
import { Invoice, InvoicePatchBody } from '../types';
import { useInvoiceSigningSession, usePatchInvoice } from '../api/hooks';
import RequestSignatureButton from '../RequestSignatureButton';
import {
    canEditInvoice,
    invoiceSignatureState,
    isSignatureRequested,
    isSigningLinkActive,
    signatureStatusLabel,
} from '../helpers';
import VoidInvoiceModal from '../VoidInvoiceModal';
import useInvoiceCustomerPhone from '../useInvoiceCustomerPhone';
import useSendInvoiceBySms from '../useSendInvoiceBySms';
import InvoiceDetails from './InvoiceDetails';
import InvoiceHistory from './InvoiceHistory';
import InvoiceNotes from './InvoiceNotes';
import SignatureDetails from '../SignatureDetails';
import {
    DrawerBody,
    DocumentActionGroup,
    PreviewPane,
    SignaturePane,
    StyledTab,
    TabLabel,
    TabsetContainer,
} from './styled';
import {
    DrawerTitle,
    DrawerTitleMain,
    DrawerTitleText,
    EmptyDescription,
    EmptyState,
    VoidMenuItem,
    SignatureStatus,
    ViewDrawer,
} from '../styled';

const PANELS = ['preview', 'sticker', 'details', 'notes', 'signature', 'history'] as const;
type Panel = typeof PANELS[number];

interface ViewInvoiceDrawerProps {
    isOpen?: boolean;
    organizationId: string;
    invoice?: Invoice;
    isLoading?: boolean;
    onRequestClose: () => void;
    onUpdated: () => void;
    onEdit?: (invoice: Invoice) => void;
}

const ViewInvoiceDrawer = ({
    isOpen,
    organizationId,
    invoice,
    isLoading,
    onRequestClose,
    onUpdated,
    onEdit,
}: ViewInvoiceDrawerProps) => {
    const { mutateAsync: patchInvoice, isLoading: isPatching } = usePatchInvoice(organizationId);
    const { isSMSEnabled } = useTwilioConversation();
    const sendInvoiceBySms = useSendInvoiceBySms(organizationId);
    const {
        customer: liveCustomer,
        phone: livePhone,
        hasPhone,
        isPending: isPhonePending,
    } = useInvoiceCustomerPhone(invoice?.customer.id);
    const [isVoidOpen, setVoidOpen] = useState(false);
    const [panel, setPanel] = useState<Panel>('preview');
    const signatureEnabled = Boolean(invoice && isSignatureRequested(invoice.document.design));
    const signingSession = useInvoiceSigningSession(
        organizationId,
        invoice?.id,
        Boolean(isOpen && signatureEnabled),
    );
    const signatureReady = !signatureEnabled || signingSession.isFetched;
    const acceptance = signingSession.data?.data.acceptance ?? null;
    const hasActiveLink = isSigningLinkActive(signingSession.data?.data.expiresAt);
    const design = invoice?.document.design;
    const signatureState = invoice && signatureReady
        ? invoiceSignatureState(invoice, acceptance, hasActiveLink)
        : 'none';
    const canRequestSignature = signatureState === 'awaiting' || signatureState === 'unrequested';
    const isVoid = invoice?.status === 'void';
    const isDraft = invoice?.status === 'draft';
    const canEdit = Boolean(
        invoice
        && canEditInvoice(
            invoice.status,
            signatureReady ? signatureState : undefined,
        ),
    );
    const canSendSms = Boolean(isSMSEnabled && invoice && !isVoid);
    const showSticker = isPrestigeInvoiceAgent(organizationId);
    const isSticker = panel === 'sticker' && showSticker;
    const canMarkPaid = !isVoid && (
        invoice?.status === 'draft'
        || invoice?.status === 'sent'
        || invoice?.status === 'overdue'
    );

    useEffect(() => {
        if (!isOpen) {
            setVoidOpen(false);
            setPanel('preview');
        }
    }, [isOpen]);

    const runCommand = async (body: InvoicePatchBody) => {
        if (!invoice) return;
        showAppSpinner();
        try {
            if (body.type === 'markPaid' && invoice.status === 'draft') {
                await patchInvoice({ invoiceId: invoice.id, body: { type: 'markSent' } });
            }
            await patchInvoice({
                invoiceId: invoice.id,
                body,
            });
            const descriptions: Record<InvoicePatchBody['type'], string> = {
                markSent: 'Invoice marked as sent.',
                markPaid: 'Invoice marked as paid.',
                void: 'Invoice marked as void.',
            };
            showAppNotification({
                title: 'Invoice updated',
                description: descriptions[body.type],
                icon: 'success',
                timeout: 4000,
            });
            onUpdated();
        } catch (error: any) {
            showAppNotification({
                title: 'Could not update invoice',
                description: error?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
        hideAppSpinner();
    };

    const handleDownload = async () => {
        if (!invoice) return;
        showAppSpinner();
        try {
            const signed = await loadInvoiceAcceptance(organizationId, invoice);
            await downloadInvoice(invoice, signed, organizationId);
        } catch (error: any) {
            showAppNotification({
                title: 'Could not download invoice',
                description: error?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
        hideAppSpinner();
    };

    const handlePrint = async () => {
        if (!invoice) return;
        showAppSpinner();
        try {
            const signed = await loadInvoiceAcceptance(organizationId, invoice);
            await printInvoice(invoice, signed, organizationId);
        } catch (error: any) {
            showAppNotification({
                title: 'Could not print invoice',
                description: error?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
        hideAppSpinner();
    };

    const handleDownloadSticker = async () => {
        if (!invoice) return;
        showAppSpinner();
        try {
            await downloadPrestigeSticker(invoice, acceptance, organizationId);
        } catch (error: any) {
            showAppNotification({
                title: 'Could not download sticker',
                description: error?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
        hideAppSpinner();
    };

    const handlePrintSticker = async () => {
        if (!invoice) return;
        showAppSpinner();
        try {
            printPdfBlob(
                await renderPrestigeStickerPdf(invoice, acceptance, organizationId),
                'Print sticker',
            );
        } catch (error: any) {
            showAppNotification({
                title: 'Could not print sticker',
                description: error?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
        hideAppSpinner();
    };

    return (
        <>
            <ViewDrawer
                $wide={showSticker && signatureState !== 'none'}
                isOpen={isOpen}
                header={(
                    <DrawerTitle>
                        <DrawerTitleMain>
                            <DrawerTitleText>
                                {invoice ? `${invoice.document.design.labels.documentTitle || 'Invoice'} ${invoice.number}` : 'Invoice'}
                            </DrawerTitleText>
                            <InvoiceStatusBadge value={invoice?.status} />
                        </DrawerTitleMain>
                        <RenderIf isTrue={signatureState !== 'none'}>
                            <SignatureStatus $signed={signatureState === 'signed'}>
                                {signatureStatusLabel(signatureState, acceptance)}
                            </SignatureStatus>
                        </RenderIf>
                    </DrawerTitle>
                )}
                slideFrom="right"
                onRequestClose={onRequestClose}
                headerActions={(
                    <RenderIf isTrue={Boolean(invoice)}>
                        <RenderIf isTrue={canRequestSignature}>
                            <RequestSignatureButton
                                organizationId={organizationId}
                                invoiceId={invoice?.id || ''}
                            />
                        </RenderIf>
                        <RenderIf isTrue={canSendSms}>
                            <ButtonIcon
                                icon={<SmsIcon />}
                                tooltip={isPhonePending || hasPhone ? 'Send by SMS' : 'This customer has no phone number'}
                                disabled={!hasPhone}
                                onClick={() => {
                                    if (!invoice || !hasPhone) return;
                                    sendInvoiceBySms({
                                        invoiceId: invoice.id,
                                        invoice,
                                        acceptance,
                                        customer: liveCustomer,
                                        phone: livePhone,
                                    });
                                }}
                            />
                        </RenderIf>
                        <ButtonIcon
                            icon={<Download />}
                            tooltip="Download invoice as pdf"
                            onClick={handleDownload}
                        />
                        <ButtonIcon
                            icon={<Printer size={18} />}
                            tooltip="Print"
                            onClick={handlePrint}
                        />
                        <RenderIf isTrue={!isVoid || canEdit}>
                            <ButtonMenu
                                menuAlignment="right"
                                menuSize="x-small"
                                buttonVariant="base"
                                icon={<DotsVertical />}
                                disabled={isPatching}
                            >
                                {canEdit && (
                                    <MenuItem
                                        label="Edit"
                                        icon={<Pencil />}
                                        onClick={() => {
                                            if (invoice) onEdit?.(invoice);
                                        }}
                                    />
                                )}
                                {isDraft && (
                                    <MenuItem
                                        label="Mark as sent"
                                        icon={<Send size={16} />}
                                        onClick={() => runCommand({ type: 'markSent' })}
                                    />
                                )}
                                {canMarkPaid && (
                                    <MenuItem
                                        label="Mark as paid"
                                        icon={<CheckCircle2 size={16} />}
                                        onClick={() => runCommand({ type: 'markPaid' })}
                                    />
                                )}
                                {!isVoid && (
                                    <VoidMenuItem
                                        label="Void invoice"
                                        icon={<Ban size={16} />}
                                        onClick={() => setVoidOpen(true)}
                                    />
                                )}
                            </ButtonMenu>
                        </RenderIf>
                    </RenderIf>
                )}
            >
                <RenderIf isTrue={isLoading}>
                    <EmptyState>
                        <Spinner />
                    </EmptyState>
                </RenderIf>
                <RenderIf isTrue={!isLoading && Boolean(invoice)}>
                    <DrawerBody>
                        <TabsetContainer>
                            <Tabset
                                fullWidth
                                variant="line"
                                activeTabName={panel}
                                onSelect={(_event, name) => {
                                    setPanel(PANELS.includes(name as Panel) ? name as Panel : 'preview');
                                }}
                            >
                                <StyledTab
                                    name="preview"
                                    label={(
                                        <TabLabel>
                                            <Eye />
                                            Preview
                                        </TabLabel>
                                    )}
                                />
                                <RenderIf isTrue={showSticker}>
                                    <StyledTab
                                        name="sticker"
                                        label={(
                                            <TabLabel>
                                                <Sticker />
                                                Sticker
                                            </TabLabel>
                                        )}
                                    />
                                </RenderIf>
                                <StyledTab
                                    name="details"
                                    label={(
                                        <TabLabel>
                                            <ListDetails />
                                            Details
                                        </TabLabel>
                                    )}
                                />
                                <StyledTab
                                    name="notes"
                                    label={(
                                        <TabLabel>
                                            <Note />
                                            Notes
                                        </TabLabel>
                                    )}
                                />
                                <RenderIf isTrue={signatureState !== 'none'}>
                                    <StyledTab
                                        name="signature"
                                        label={(
                                            <TabLabel>
                                                <Signature />
                                                Signature
                                            </TabLabel>
                                        )}
                                    />
                                </RenderIf>
                                <StyledTab
                                    name="history"
                                    label={(
                                        <TabLabel>
                                            <History />
                                            History
                                        </TabLabel>
                                    )}
                                />
                            </Tabset>
                        </TabsetContainer>
                        <RenderIf isTrue={panel === 'preview'}>
                            <PreviewPane>
                                {invoice && signatureReady ? (
                                    <InvoiceDocumentView
                                        invoice={invoice}
                                        acceptance={acceptance}
                                        organizationId={organizationId}
                                    />
                                ) : (
                                    <EmptyState>
                                        <Spinner />
                                    </EmptyState>
                                )}
                                <DocumentActionGroup>
                                    <ButtonIcon
                                        size="small"
                                        variant="base"
                                        borderRadius="semi-rounded"
                                        icon={<Printer size={18} />}
                                        tooltip="Print"
                                        onClick={handlePrint}
                                    />
                                    <ButtonIcon
                                        size="small"
                                        variant="base"
                                        borderRadius="semi-rounded"
                                        icon={<Download />}
                                        tooltip="Download"
                                        onClick={handleDownload}
                                    />
                                </DocumentActionGroup>
                            </PreviewPane>
                        </RenderIf>
                        <RenderIf isTrue={isSticker && Boolean(invoice)}>
                            <PreviewPane>
                                <InvoiceDocumentView
                                    invoice={invoice as Invoice}
                                    organizationId={organizationId}
                                    renderPdf={renderPrestigeStickerPdf}
                                />
                                <DocumentActionGroup>
                                    <ButtonIcon
                                        size="small"
                                        variant="base"
                                        borderRadius="semi-rounded"
                                        icon={<Printer size={18} />}
                                        tooltip="Print sticker"
                                        onClick={handlePrintSticker}
                                    />
                                    <ButtonIcon
                                        size="small"
                                        variant="base"
                                        borderRadius="semi-rounded"
                                        icon={<Download />}
                                        tooltip="Download sticker"
                                        onClick={handleDownloadSticker}
                                    />
                                </DocumentActionGroup>
                            </PreviewPane>
                        </RenderIf>
                        <RenderIf isTrue={panel === 'details' && Boolean(invoice)}>
                            <InvoiceDetails invoice={invoice as Invoice} />
                        </RenderIf>
                        <RenderIf isTrue={panel === 'notes' && Boolean(invoice)}>
                            <InvoiceNotes
                                organizationId={organizationId}
                                invoice={invoice as Invoice}
                                onUpdated={onUpdated}
                            />
                        </RenderIf>
                        <RenderIf isTrue={panel === 'history' && Boolean(invoice)}>
                            {invoice ? (
                                <InvoiceHistory
                                    organizationId={organizationId}
                                    invoiceId={invoice.id}
                                    currency={(
                                        invoice.document.totals.currency
                                        || invoice.document.design.regional.currency
                                    )}
                                    locale={invoice.document.design.regional.locale}
                                />
                            ) : null}
                        </RenderIf>
                        <RenderIf isTrue={panel === 'signature' && signatureState !== 'none'}>
                            <SignaturePane>
                                <SignatureDetails
                                    state={signatureState === 'none' ? 'unrequested' : signatureState}
                                    acceptance={acceptance}
                                    locale={design?.regional.locale}
                                    showTitle={false}
                                />
                            </SignaturePane>
                        </RenderIf>
                    </DrawerBody>
                </RenderIf>
                <RenderIf isTrue={!isLoading && !invoice}>
                    <EmptyState>
                        <EmptyDescription>Invoice could not be loaded.</EmptyDescription>
                    </EmptyState>
                </RenderIf>
            </ViewDrawer>
            <VoidInvoiceModal
                isOpen={isVoidOpen}
                invoiceNumber={invoice?.number}
                isSubmitting={isPatching}
                onRequestClose={() => setVoidOpen(false)}
                onConfirm={(note) => {
                    setVoidOpen(false);
                    runCommand({ type: 'void', note });
                }}
            />
        </>
    );
};

export default ViewInvoiceDrawer;
